/**
 * Cloudflare R2 from the browser, over the S3-compatible API.
 *
 * Credentials live in this browser and nowhere else. There is no server in this
 * path: the page signs each request itself and talks to the user's bucket
 * directly, so an envelope goes from the tab that encrypted it straight into
 * storage the user owns.
 *
 * SigV4 is implemented here rather than pulled from the AWS SDK — the signing
 * is about eighty lines of HMAC, and the SDK would add hundreds of kilobytes to
 * a page whose whole pitch is that you can read what it does.
 */

const SERVICE = 's3';
const REGION = 'auto'; // R2 ignores region, but SigV4 requires one in the scope.

export interface R2Config {
  accountId: string;
  accessKeyId: string;
  secretAccessKey: string;
  bucket: string;
}

export interface R2Object {
  key: string;
  size: number;
  lastModified: string;
}

const enc = new TextEncoder();

function hex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(data: BufferSource | string): Promise<string> {
  const bytes = typeof data === 'string' ? enc.encode(data) : data;
  return hex(await crypto.subtle.digest('SHA-256', bytes));
}

async function hmac(key: ArrayBuffer | Uint8Array, msg: string): Promise<ArrayBuffer> {
  const k = await crypto.subtle.importKey(
    'raw',
    key as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  return crypto.subtle.sign('HMAC', k, enc.encode(msg));
}

/** AWS4 signing key: a chain of HMACs over date, region, service. */
async function signingKey(secret: string, date: string): Promise<ArrayBuffer> {
  return signingKeyFor(secret, date, REGION, SERVICE);
}

/**
 * Exported for verification against AWS's published SigV4 test vectors. A
 * signing bug would otherwise only ever surface as an opaque 403 from R2, long
 * after the credentials were entered.
 */
export const __sigv4 = {
  sha256Hex,
  signingKey: (secret: string, date: string, region: string, service: string) =>
    signingKeyFor(secret, date, region, service),
  hmac,
  hex,
};

async function signingKeyFor(
  secret: string,
  date: string,
  region: string,
  service: string
): Promise<ArrayBuffer> {
  let k: ArrayBuffer | Uint8Array = enc.encode('AWS4' + secret);
  for (const part of [date, region, service, 'aws4_request']) k = await hmac(k, part);
  return k as ArrayBuffer;
}

export function r2Host(cfg: R2Config): string {
  return `${cfg.accountId}.r2.cloudflarestorage.com`;
}

/**
 * Sign and send one request.
 *
 * `host` is signed but never set via fetch — browsers forbid it, and set it
 * themselves to the same value, so the signature still matches.
 */
async function signedFetch(
  cfg: R2Config,
  method: string,
  key: string,
  body?: BufferSource
): Promise<Response> {
  const host = r2Host(cfg);
  // Each path segment is encoded separately so that slashes inside a key stay
  // meaningful while everything else is escaped.
  const path =
    '/' + [cfg.bucket, ...key.split('/')].filter(Boolean).map(encodeURIComponent).join('/');

  const now = new Date();
  const amzDate = now.toISOString().replace(/[:-]|\.\d{3}/g, '');
  const dateStamp = amzDate.slice(0, 8);

  const payloadHash = await sha256Hex(body ?? '');

  const headers: Record<string, string> = {
    host,
    'x-amz-content-sha256': payloadHash,
    'x-amz-date': amzDate,
  };

  const signedHeaders = Object.keys(headers).sort().join(';');
  const canonicalHeaders = Object.keys(headers)
    .sort()
    .map((h) => `${h}:${headers[h]}\n`)
    .join('');

  const canonicalRequest = [
    method,
    path,
    '', // no query string
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join('\n');

  const scope = `${dateStamp}/${REGION}/${SERVICE}/aws4_request`;
  const stringToSign = [
    'AWS4-HMAC-SHA256',
    amzDate,
    scope,
    await sha256Hex(canonicalRequest),
  ].join('\n');

  const signature = hex(await hmac(await signingKey(cfg.secretAccessKey, dateStamp), stringToSign));

  return fetch(`https://${host}${path}`, {
    method,
    // `host` is dropped: the browser sets it and refuses an explicit one.
    headers: {
      'x-amz-content-sha256': payloadHash,
      'x-amz-date': amzDate,
      Authorization:
        `AWS4-HMAC-SHA256 Credential=${cfg.accessKeyId}/${scope}, ` +
        `SignedHeaders=${signedHeaders}, Signature=${signature}`,
    },
    body,
  });
}

/** Thrown when the browser was blocked before the request left, i.e. CORS. */
export class R2CorsError extends Error {
  constructor() {
    super('The bucket is not allowing requests from this page yet.');
    this.name = 'R2CorsError';
  }
}

async function call(
  cfg: R2Config,
  method: string,
  key: string,
  body?: BufferSource
): Promise<Response> {
  let res: Response;
  try {
    res = await signedFetch(cfg, method, key, body);
  } catch {
    // fetch only rejects for network-level failures; a blocked preflight is by
    // far the likeliest cause here, and is fixable, so name it specifically
    // rather than reporting a generic network error.
    throw new R2CorsError();
  }
  if (!res.ok && res.status !== 404) {
    const detail = await res.text().catch(() => '');
    const code = /<Code>([^<]+)<\/Code>/.exec(detail)?.[1];
    throw new Error(
      code
        ? `R2 rejected the request: ${code}`
        : `R2 returned ${res.status}${detail ? ` — ${detail.slice(0, 120)}` : ''}`
    );
  }
  return res;
}

export async function putObject(cfg: R2Config, key: string, body: BufferSource): Promise<void> {
  await call(cfg, 'PUT', key, body);
}

export async function getObject(cfg: R2Config, key: string): Promise<ArrayBuffer | null> {
  const res = await call(cfg, 'GET', key);
  return res.status === 404 ? null : res.arrayBuffer();
}

export async function deleteObject(cfg: R2Config, key: string): Promise<void> {
  await call(cfg, 'DELETE', key);
}

export async function headObject(cfg: R2Config, key: string): Promise<number | null> {
  const res = await call(cfg, 'HEAD', key);
  if (res.status === 404) return null;
  return Number(res.headers.get('content-length') ?? 0);
}

export interface ConnectionTest {
  ok: boolean;
  steps: { label: string; ok: boolean; detail?: string }[];
  corsBlocked: boolean;
}

/**
 * Prove the bucket really works before anything is trusted to it.
 *
 * Writes a probe object, reads it back, compares the bytes, then deletes it. A
 * write that succeeds but reads back wrong is worse than one that fails, so the
 * comparison matters as much as the status codes — and the probe is removed so
 * a test leaves nothing behind.
 */
export async function testConnection(cfg: R2Config): Promise<ConnectionTest> {
  const steps: ConnectionTest['steps'] = [];
  const key = `.aliveguardian-probe-${Date.now()}`;
  const payload = enc.encode(`probe ${new Date().toISOString()}`);
  let corsBlocked = false;

  const run = async (label: string, fn: () => Promise<string | undefined>) => {
    try {
      const detail = await fn();
      steps.push({ label, ok: true, detail });
      return true;
    } catch (e) {
      if (e instanceof R2CorsError) corsBlocked = true;
      steps.push({ label, ok: false, detail: e instanceof Error ? e.message : String(e) });
      return false;
    }
  };

  if (!(await run('Write a test file', async () => {
    await putObject(cfg, key, payload);
    return `${payload.byteLength} bytes`;
  }))) {
    return { ok: false, steps, corsBlocked };
  }

  const readOk = await run('Read it back', async () => {
    const got = await getObject(cfg, key);
    if (!got) throw new Error('The file was written but could not be read back.');
    const same =
      got.byteLength === payload.byteLength &&
      new Uint8Array(got).every((b, i) => b === payload[i]);
    if (!same) throw new Error('What came back did not match what was written.');
    return 'contents match';
  });

  const cleanOk = await run('Remove the test file', async () => {
    await deleteObject(cfg, key);
    return undefined;
  });

  return { ok: readOk && cleanOk, steps, corsBlocked };
}

/** The CORS policy an R2 bucket needs before a browser may write to it. */
export function corsPolicy(origin: string): string {
  return JSON.stringify(
    [
      {
        AllowedOrigins: [origin],
        AllowedMethods: ['GET', 'PUT', 'DELETE', 'HEAD'],
        AllowedHeaders: ['*'],
        ExposeHeaders: ['ETag', 'Content-Length'],
        MaxAgeSeconds: 3600,
      },
    ],
    null,
    2
  );
}

/** Where the user creates an R2 token and where the bucket's CORS settings live. */
export function dashboardLinks(cfg: Pick<R2Config, 'accountId' | 'bucket'>) {
  const base = `https://dash.cloudflare.com/${cfg.accountId || ''}`;
  return {
    tokens: `${base}/r2/api-tokens`,
    bucketSettings: cfg.bucket ? `${base}/r2/default/buckets/${cfg.bucket}/settings` : `${base}/r2`,
    overview: `${base}/r2`,
  };
}
